import { useAtomValue } from 'jotai';
import { useEffect, useMemo } from 'react';

import { useStores } from '@components/common/root-store-provider/root-store-provider';
import { useAnalytics } from '@features/Analytics';
import { tikTokAdvancedMatching, tikTokEvent } from '@features/Analytics';
import {
  getFacebookPurchaseCustomData,
  getFacebookPurchaseParameters,
  getFacebookSubscribeCustomData,
} from '@features/Analytics/services/facebook-ltv';
import { twitterEvent } from '@features/Analytics/services/twitter-pixel';
import {
  createAppLovinEcommercePayload,
  getAppLovinActualPrice,
} from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';
import { selectedPlanAtom } from '@features/Stripe/atoms';
import { useGetStripeProductId } from '@features/Stripe/hooks/query/use-get-stripe-product-id';
import { getDataFromSessionStorage } from '@features/Stripe/utils/get-data-from-session-storage';

type UsePaymentAnalyticsProps = {
  isRedirectStatusSucceeded: boolean;
  isRedirectStatusFailed: boolean;

  analyticsCombinedParameters: Record<string, any>;
  shouldSendEvent?: boolean;
  setPaymentError: (callback: (previous: any) => any) => void;
};

export const usePaymentAnalytics = ({
  isRedirectStatusSucceeded,
  isRedirectStatusFailed,
  analyticsCombinedParameters,
  shouldSendEvent,

  setPaymentError,
}: UsePaymentAnalyticsProps) => {
  const {
    authStore: { variant, user },
  } = useStores();

  const planAtom = useAtomValue(selectedPlanAtom);
  const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');
  const { predictedLtv } = useGetStripeProductId();

  const {
    trackGoogleEvent,
    trackFacebookEvent,
    trackPinterestEvent,
    trackAppLovin,
  } = useAnalytics();
  const isIntroOfferFeatureEnabled = useFeature('introOffer');
  const isIntroOfferEnabled =
    Boolean(isIntroOfferFeatureEnabled) || variant === 'email_ng';

  const facebookPurchaseParameters = useMemo(
    () =>
      getFacebookPurchaseParameters({
        analyticsParameters: analyticsCombinedParameters,
        predictedLtv,
      }),
    [analyticsCombinedParameters, predictedLtv],
  );

  const facebookPurchaseCustomData = useMemo(
    () =>
      getFacebookPurchaseCustomData({
        predictedLtv,
        purchaseParameters: facebookPurchaseParameters,
      }),
    [facebookPurchaseParameters, predictedLtv],
  );

  const facebookSubscribeCustomData = useMemo(
    () =>
      getFacebookSubscribeCustomData({
        analyticsParameters: analyticsCombinedParameters,
        predictedLtv,
      }),
    [analyticsCombinedParameters, predictedLtv],
  );

  useEffect(() => {
    if (
      isRedirectStatusSucceeded &&
      planAtom.priceId != '' &&
      shouldSendEvent
    ) {
      trackFacebookEvent({
        eventName: 'Purchase',
        customData: {
          variant: variant,
          paymentSystem: 'stripe',
          paymentMethod: 'paypal',
          ...facebookPurchaseCustomData,
        },
        options: {
          ...facebookPurchaseParameters,
          email: user?.email,
          subscription_id: undefined,
          stripe_subscription_id: planAtom.priceId,
        },
      });
      !fbPixelSept2025Feature &&
        trackFacebookEvent({
          eventName: 'Subscribe',
          customData: {
            variant: variant,
            paymentSystem: 'stripe',
            paymentMethod: 'paypal',
            ...facebookSubscribeCustomData,
          },
          options: {
            email: user?.email,
            stripe_subscription_id: planAtom.priceId,
          },
        });
      trackPinterestEvent({
        eventName: 'Checkout',
        options: { ...analyticsCombinedParameters },
      });

      twitterEvent('example2', {
        email: user?.email,
        ...analyticsCombinedParameters,
      });

      tikTokAdvancedMatching(user?.email ?? '');
      tikTokEvent('CompletePayment', {
        content_id: planAtom?.planName,
        ...analyticsCombinedParameters,
        quantity: 1,
      });
      trackGoogleEvent({
        eventName: 'purchase_success',
        options: {
          product_id: planAtom?.planName,
          payment_type: 'paypal',
          campaign_name_manual:
            getDataFromSessionStorage({
              store: 'authStore',
              key: 'campaign',
            }) ?? 'undefined',
          email: user?.email,
          ...analyticsCombinedParameters,
        },
      });
      const appLovinActualPrice = getAppLovinActualPrice({
        fullPrice: planAtom.fullPrice,
        fullPriceDiscount: planAtom.fullPriceDiscount,
        introOfferData: planAtom.introOfferData,
        isIntroOfferEnabled,
      });
      trackAppLovin({
        eventName: 'purchase',
        options: createAppLovinEcommercePayload({
          currency: planAtom.introOfferData?.currency || planAtom.currency,
          value: appLovinActualPrice,
          itemId: planAtom.priceId,
          itemName: planAtom.planName,
          transactionId: planAtom.priceId,
          paymentMethod: 'paypal',
          paymentSystem: 'stripe',
          userEmail: user?.email,
          userId: user?.serverId,
          variant,
        }),
      });
      trackAppLovin({
        eventName: 'subscribe',
        options: createAppLovinEcommercePayload({
          currency: planAtom.introOfferData?.currency || planAtom.currency,
          value: appLovinActualPrice,
          itemId: planAtom.priceId,
          itemName: planAtom.planName,
          transactionId: planAtom.priceId,
          paymentMethod: 'paypal',
          paymentSystem: 'stripe',
          userEmail: user?.email,
          userId: user?.serverId,
          variant,
        }),
      });
    }
    if (isRedirectStatusFailed && shouldSendEvent) {
      setPaymentError((previous) => ({
        ...previous,
        errorCode: 'insufficient_funds',
        paymentSystem: 'PayPal',
        paymentMethod: 'paypal',
      }));
      trackGoogleEvent({
        eventName: 'purchase_subscription_failed',
        options: {
          payment_type: 'paypal',
          campaign_name_manual:
            getDataFromSessionStorage({
              store: 'authStore',
              key: 'campaign',
            }) ?? 'undefined',
          product_id: planAtom?.planName ?? '',
        },
      });
    }
  }, [
    analyticsCombinedParameters,
    facebookPurchaseCustomData,
    facebookPurchaseParameters,
    facebookSubscribeCustomData,
    fbPixelSept2025Feature,
    isIntroOfferEnabled,
    isRedirectStatusFailed,
    isRedirectStatusSucceeded,
    planAtom,
    setPaymentError,
    shouldSendEvent,
    trackFacebookEvent,
    trackAppLovin,
    trackGoogleEvent,
    trackPinterestEvent,
    user?.email,
    user?.serverId,
    variant,
  ]);
};
